import { motion } from "framer-motion";
import { TrendingUp, Target, Brain, MessageSquare, Award } from "lucide-react";
import { useCountUp } from "@/hooks/useCountUp";

const metrics = [
  { label: "Confidence", value: 87, color: "#635BFF" },
  { label: "Persuasion", value: 72, color: "#818cf8" },
  { label: "Empathy", value: 91, color: "#10b981" },
  { label: "Strategy", value: 68, color: "#00D4FF" },
  { label: "Clarity", value: 83, color: "#f59e0b" },
];

const coachingInsights = [
  { icon: Target, text: "Strong opening anchor — market data reference was effective", type: "positive" },
  { icon: Brain, text: "Consider acknowledging their constraints before countering", type: "suggestion" },
  { icon: MessageSquare, text: "Your tone shifted to defensive after the pushback — try staying curious", type: "improvement" },
  { icon: Award, text: "Excellent use of the 'mutual gains' framework in closing", type: "positive" },
];

function AnimatedScore() {
  const { count, ref } = useCountUp(82, 1800);
  return (
    <div ref={ref} className="flex items-end gap-2">
      <span className="font-heading text-5xl font-bold text-navy">{count}</span>
      <span className="text-sm text-stripe-text-muted mb-2">/100</span>
    </div>
  );
}

export default function PerformanceFeedbackSection() {
  return (
    <section data-testid="performance-section" className="py-[45px] md:py-[90px] stripe-bg relative">
      <div className="absolute inset-0 section-glow-right" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="section-overline mb-4">Performance Feedback</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">
              Data-driven coaching{" "}
              <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">
                after every session
              </span>
            </h2>
            <p className="text-base md:text-lg text-stripe-text-secondary leading-relaxed mb-10">
              No more guesswork. Get precise scoring across multiple dimensions, 
              actionable coaching tips, and track your improvement over time.
            </p>

            {/* Coaching insights */}
            <div className="space-y-3">
              {coachingInsights.map((insight, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border ${
                    insight.type === "positive"
                      ? "bg-emerald-50/50 border-emerald-100"
                      : insight.type === "suggestion"
                      ? "bg-amber-50/50 border-amber-100"
                      : "bg-slate-50 border-stripe-border"
                  }`}
                >
                  <insight.icon
                    className={`w-4 h-4 mt-0.5 shrink-0 ${
                      insight.type === "positive"
                        ? "text-emerald-600"
                        : insight.type === "suggestion"
                        ? "text-amber-600"
                        : "text-stripe-text-muted"
                    }`}
                  />
                  <span className="text-sm text-stripe-text-secondary">{insight.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Visual scorecard */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="stripe-card rounded-2xl p-8">
              {/* Score header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="text-xs text-stripe-text-muted mb-1">Overall Score</div>
                  <AnimatedScore />
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-100">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-600">+12 from last</span>
                </div>
              </div>

              {/* Skill bars */}
              <div className="space-y-5">
                {metrics.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-stripe-text-secondary">{m.label}</span>
                      <span className="text-sm font-bold text-navy">{m.value}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: m.color }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${m.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Session history chart */}
              <div className="mt-8 pt-6 border-t border-stripe-border">
                <div className="text-xs text-stripe-text-muted mb-3">Last 7 Sessions</div>
                <div className="flex items-end gap-2 h-20">
                  {[52, 58, 64, 71, 68, 78, 82].map((v, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 rounded-t-sm relative"
                      style={{ background: `rgba(99,91,255,${0.2 + (i / 7) * 0.8})` }}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${(v / 100) * 80}px` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
